import { DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { MetersService } from '@app/pages/apps/meters/services/meters.service';
import type {
  Meter,
  CreateMeterRequest,
  UpdateMeterRequest,
  AddMeterReadingRequest,
  MeterDocument,
  ContractHolder
} from '../models/meter.model';
import type { IMetersStore } from './meters-store.interface';

/**
 * Firestore-backed implementation of `IMetersStore`.
 *
 * Thin adapter over the legacy `MetersService` (which still owns the actual
 * Firestore reads/writes). This keeps the blast radius small: all the
 * permission/path logic continues to live in `MetersService`, and the store
 * just exposes it through the signal API that consumers expect.
 *
 * Eventually `MetersService` can be retired once its last non-Pattern-B
 * consumers are migrated, and the Firestore logic will move here directly.
 */
@Injectable()
export class MetersFirestoreStore implements IMetersStore {
  private readonly metersService = inject(MetersService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _currentPropertyUID = signal<string | null>(null);
  private readonly _meters = signal<readonly Meter[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);
  private metersSub?: { unsubscribe(): void };
  private loadingSub?: { unsubscribe(): void };

  readonly currentPropertyUID = this._currentPropertyUID.asReadonly();
  readonly meters = this._meters.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._meters().length);

  readonly metersByType = computed<Readonly<Record<string, number>>>(() =>
    this._meters().reduce<Record<string, number>>((acc, m) => {
      acc[m.type] = (acc[m.type] || 0) + 1;
      return acc;
    }, {})
  );

  readonly activeMeters = computed(() => this._meters().filter(m => m.status === 'active').length);
  readonly tenantMeters = computed(() => this._meters().filter(m => m.contractHolder === 'tenant').length);
  readonly ownerMeters = computed(() => this._meters().filter(m => m.contractHolder === 'owner').length);

  readonly metersNeedingAttention: Signal<{
    readonly noRecentReadings: readonly Meter[];
    readonly inactive: readonly Meter[];
  }> = computed(() => {
    const meters = this._meters();
    const threeMonthsAgo = Date.now() - 90 * 24 * 60 * 60 * 1000;
    return {
      noRecentReadings: meters.filter(m => {
        if (!m.currentReadingDate) return true;
        return m.currentReadingDate.toMillis() < threeMonthsAgo;
      }),
      inactive: meters.filter(m => m.status !== 'active')
    };
  });

  loadForProperty(propertyUID: string): void {
    if (this._currentPropertyUID() === propertyUID) return;
    this._currentPropertyUID.set(propertyUID);
    this._lastError.set(null);

    // Swap subscriptions to the new property stream. MetersService internally
    // tracks "currentPropertyUID" and only re-fetches when it actually changes.
    this.metersSub?.unsubscribe();
    this.loadingSub?.unsubscribe();

    this.metersSub = this.metersService.getMetersByProperty(propertyUID)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: meters => this._meters.set(meters),
        error: err => this._lastError.set(err instanceof Error ? err : new Error(String(err)))
      });
    this.loadingSub = this.metersService.loading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(v => this._loading.set(v));
  }

  async refresh(): Promise<void> {
    const pid = this._currentPropertyUID();
    if (!pid) return;
    // Force a reload by flushing the cached property id in the underlying service.
    // The simplest/safest way is to re-trigger loadForProperty with the same id
    // after clearing — but MetersService short-circuits same-id calls. Instead
    // we temporarily swap to a sentinel id and back.
    this._currentPropertyUID.set(null);
    this.loadForProperty(pid);
  }

  async createMeter(request: CreateMeterRequest): Promise<string> {
    try {
      return await firstValueFrom(this.metersService.createMeter(request));
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async updateMeter(meterUID: string, updates: UpdateMeterRequest): Promise<void> {
    const pid = this.requirePropertyId();
    try {
      await firstValueFrom(this.metersService.updateMeter(pid, meterUID, updates));
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async deleteMeter(meterUID: string): Promise<void> {
    const pid = this.requirePropertyId();
    try {
      await firstValueFrom(this.metersService.deleteMeter(pid, meterUID));
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async addReading(meterUID: string, reading: AddMeterReadingRequest): Promise<void> {
    const pid = this.requirePropertyId();
    try {
      await firstValueFrom(this.metersService.addMeterReading(pid, meterUID, reading));
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async transferContract(meterUID: string, newHolder: ContractHolder): Promise<void> {
    const pid = this.requirePropertyId();
    try {
      await firstValueFrom(
        this.metersService.transferMeterContract(
          pid,
          meterUID,
          // MetersService only accepts owner|tenant; guard 'unknown' at the boundary.
          newHolder === 'unknown' ? 'owner' : newHolder
        )
      );
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async addDocument(meterUID: string, document: Omit<MeterDocument, 'id' | 'uploadDate'>): Promise<void> {
    const pid = this.requirePropertyId();
    try {
      await firstValueFrom(this.metersService.addMeterDocument(pid, meterUID, document));
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async addPhoto(meterUID: string, file: File): Promise<void> {
    return this.addFile(meterUID, file, 'photo');
  }

  async addFile(meterUID: string, file: File, type: MeterDocument['type']): Promise<void> {
    const pid = this.requirePropertyId();
    try {
      await firstValueFrom(this.metersService.uploadMeterFile(pid, meterUID, file, type));
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async removeDocument(meterUID: string, documentId: string): Promise<void> {
    const pid = this.requirePropertyId();
    try {
      await firstValueFrom(this.metersService.removeMeterDocument(pid, meterUID, documentId));
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  isEanCodeUnique(eanCode: string, excludeMeterUID?: string): boolean {
    if (!eanCode) return true;
    return !this._meters().some(
      m => m.eanCode === eanCode && (!excludeMeterUID || m.meterUID !== excludeMeterUID)
    );
  }

  getMeterById(meterUID: string): Meter | null {
    return this._meters().find(m => m.meterUID === meterUID) ?? null;
  }

  private requirePropertyId(): string {
    const pid = this._currentPropertyUID();
    if (!pid) throw new Error('MetersFirestoreStore: no property loaded; call loadForProperty() first');
    return pid;
  }
}
